import { t } from '@lingui/core/macro';
import { noddiToast } from 'noddi-ui-web';
import { getFirstErrorCode, isAxiosError } from 'noddi-util';
import type { StationarySlotHold, TimeSlot } from 'noddi-web';
import {
  customerWeekCapacityRange,
  useShowSlotAvailability,
  useStationaryCapacityRange,
  useStationaryHoldOnSelect
} from 'noddi-web';

type Props = {
  garageId?: number;
  salesItemId?: number;
  addonSalesItemIds?: number[];
  maxDaysAhead?: number;
  /** `yyyy-MM-dd`. The picker opens on the week that contains this date. */
  initialDate?: string | null;
  reservation: StationarySlotHold | null;
  onReservationChange: (reservation: StationarySlotHold | null) => void;
};

/** Stationary capacity week + hold-on-select via shared {@link StationarySlotHold}. */
export const useStationaryTimeSlotPicker = ({
  garageId,
  salesItemId,
  addonSalesItemIds,
  maxDaysAhead,
  initialDate,
  reservation,
  onReservationChange
}: Props) => {
  const showSlotAvailability = useShowSlotAvailability();
  const salesItemIds = salesItemId != null ? [salesItemId, ...(addonSalesItemIds ?? [])] : undefined;
  const isEnabled = garageId != null && salesItemId != null;

  const week = useStationaryCapacityRange({
    garageId,
    salesItemIds,
    detailed: showSlotAvailability,
    enabled: isEnabled,
    range: customerWeekCapacityRange(maxDaysAhead),
    initialDate,
    // Customers reserve straight off these tiles, so a stale week is a 409 waiting to happen.
    keepFresh: true,
    heldWindow: reservation
      ? { start: reservation.startsAt, end: reservation.endsAt, workstationId: reservation.workstationId }
      : null
  });

  const reserveSalesItemIds = salesItemId != null ? [salesItemId, ...(addonSalesItemIds ?? [])] : undefined;

  const { handleSelectSlot, isReservationPending, pendingSlotId, selectedSlotId } = useStationaryHoldOnSelect({
    garageId,
    salesItemIds: reserveSalesItemIds,
    reservation,
    onReservationChange,
    slotLookup: week.slotLookup,
    resolveSelectedSlotId: week.resolveSlotId,
    onReserveFailed: (error) => {
      // "Taken" only for the actual race; an outage told as "taken" sends the customer
      // hunting through slots that all fail the same way.
      if (isSlotTakenError(error)) {
        noddiToast.error(t`That time is no longer available. Please choose another slot.`);
        return;
      }
      noddiToast.error(t`Something went wrong. Please try again.`);
    }
  });

  return {
    days: week.days,
    // The garage's zone, not the browser's — a recap of the held slot must read the
    // same as the tile the customer tapped.
    timeZone: week.timeZone,
    selectedSlotId,
    handleSelectSlot: (timeSlot: TimeSlot) => {
      void handleSelectSlot(timeSlot);
    },
    goToPreviousRange: week.goToPreviousRange,
    goToNextRange: week.goToNextRange,
    isPreviousDisabled: week.isPreviousDisabled,
    isNextDisabled: week.isNextDisabled,
    dateRangeLabel: week.dateRangeLabel,
    isPending: week.isPending,
    isReservationPending,
    pendingSlotId,
    error: week.error,
    translations: {
      previous: t`Previous Week`,
      next: t`Next Week`,
      emptyState: t`No time slots available for this week`,
      loading: t`Loading`
    }
  };
};

const SLOT_TAKEN_ERROR_CODES = ['workstation_slot_unavailable_api_error', 'eontyre_time_slot_unavailable_error'];

const isSlotTakenError = (error: unknown) =>
  isAxiosError(error) && SLOT_TAKEN_ERROR_CODES.includes(getFirstErrorCode(error) ?? '');
